import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Markdown from "react-markdown";
import { X } from "lucide-react";
import { DataActQuestion, DataActOption, DataActAnswer } from "../types";
import PreviousAnswerPopover from "./assessment/PreviousAnswerPopover";

interface DataActAssessmentQuestionProps {
  question: DataActQuestion;
  answer: string;
  onChange: (
    questionId: string,
    field: "answer" | "details",
    value: string,
  ) => void;
  disabled?: boolean;
  previousAnswer?: DataActAnswer | null | undefined;
  previousVersion?: number | null;
  pathKey?: string | null;
}

function resolveLabel(
  question: DataActQuestion,
  pathKey?: string | null,
): string {
  const labels = question.questionLabel;
  if (labels && pathKey) {
    const value = (labels as Record<string, string | undefined>)[pathKey];
    if (value) return value;
  }
  return question.id;
}

export default function DataActAssessmentQuestion({
  question,
  answer,
  onChange,
  disabled = false,
  previousAnswer = null,
  previousVersion = null,
  pathKey = null,
}: DataActAssessmentQuestionProps) {
  const hasPrevious = previousAnswer && previousAnswer.answer;
  const displayLabel = resolveLabel(question, pathKey);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    if (!lightboxImage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxImage]);

  const renderOptionInfo = (
    description: string,
    idx: number,
    total: number,
  ) => {
    const anchor = idx === total - 1 && total > 1 ? "right-0" : "left-0";
    return (
      <span className="relative group/info cursor-help shrink-0 inline-flex items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5 opacity-70"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
        <span
          className={`invisible group-hover/info:visible hover:visible absolute ${anchor} top-full mt-1 z-50 w-64 rounded-md bg-gray-900 dark:bg-gray-700 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed p-2.5 shadow-lg whitespace-normal text-left pointer-events-auto before:content-[''] before:absolute before:left-0 before:right-0 before:-top-1 before:h-1`}
          onClick={(e) => e.stopPropagation()}
        >
          <Markdown
            components={{
              p: ({ children }) => (
                <p className="text-[11px] leading-relaxed text-white mb-1 last:mb-0">
                  {children}
                </p>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-300 underline hover:text-indigo-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  {children}
                </a>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
            }}
          >
            {description}
          </Markdown>
        </span>
      </span>
    );
  };

  const renderOptions = (options: DataActOption[]) => {
    if (question.type === "three_way_choice") {
      return (
        <div
          className={`grid gap-2`}
          style={{
            gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
          }}
        >
          {options.map((opt, idx) => (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(question.id, "answer", opt.value)}
              className={`py-2.5 px-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center justify-center gap-1.5 ${
                answer === opt.value
                  ? opt.value === "yes" || opt.value.startsWith("yes")
                    ? "bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200"
                    : opt.value === "no"
                      ? "bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200"
                      : "bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-200"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="min-w-0 truncate">{opt.label}</span>
              {opt.description &&
                renderOptionInfo(opt.description, idx, options.length)}
            </button>
          ))}
        </div>
      );
    }

    return (
      <div
        className={`grid gap-2`}
        style={{
          gridTemplateColumns: `repeat(${options.length <= 3 ? options.length : 1}, minmax(0, 1fr))`,
        }}
      >
        {options.map((opt, idx) => (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(question.id, "answer", opt.value)}
            className={`py-2.5 px-3 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
              options.length <= 3 ? "justify-center" : "justify-start text-left"
            } ${
              answer === opt.value
                ? "bg-primary-50 border-primary-300 text-primary-800 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-200"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span
              className={options.length <= 3 ? "min-w-0 truncate" : "min-w-0"}
            >
              {opt.label}
            </span>
            {opt.description &&
              renderOptionInfo(opt.description, idx, options.length)}
          </button>
        ))}
      </div>
    );
  };

  if (question.infoOnly) {
    const displayValue =
      question.options?.find((o) => o.value === answer)?.label ?? answer;
    return (
      <div className="w-full">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {displayLabel}. {question.title}
            </h4>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {displayValue || "—"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {displayLabel}. {question.title}
            </h4>
            {question.description && (
              <div className="mt-1.5">
                <Markdown
                  components={{
                    p: ({ children }) => (
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-1 last:mb-0">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-700 dark:text-gray-300">
                        {children}
                      </strong>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-0.5 text-sm text-gray-600 dark:text-gray-400 mb-1 last:mb-0">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-0.5 text-sm text-gray-600 dark:text-gray-400 mb-1 last:mb-0">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="leading-relaxed">{children}</li>
                    ),
                  }}
                >
                  {question.description}
                </Markdown>
              </div>
            )}
          </div>
        </div>

        {question.guidance && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-md">
            <h5 className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-1.5">
              Guidance
            </h5>
            <Markdown
              components={{
                p: ({ children }) => (
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2 last:mb-0">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-gray-900 dark:text-gray-100">
                    {children}
                  </strong>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 mb-2 last:mb-0">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 mb-2 last:mb-0 marker:font-semibold marker:text-gray-900 dark:marker:text-gray-100">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="leading-relaxed">{children}</li>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {question.guidance}
            </Markdown>
            {question.guidanceImages && question.guidanceImages.length > 0 && (
              <div className="mt-3 space-y-3">
                {question.guidanceImages.map((img) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() =>
                      setLightboxImage(
                        `/api/assessments/data-act/images/${img}`,
                      )
                    }
                    className="block w-full text-left cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    aria-label={`Open guidance image for question ${displayLabel} fullscreen`}
                  >
                    <img
                      src={`/api/assessments/data-act/images/${img}`}
                      alt={`Guidance for question ${displayLabel}`}
                      className="max-w-full rounded border border-blue-200 dark:border-blue-700 hover:opacity-90 transition-opacity"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Answer{" "}
              {question.required && <span className="text-red-500">*</span>}
            </label>
            {hasPrevious && previousVersion != null && (
              <PreviousAnswerPopover
                label="Answer"
                value={
                  question.options?.find(
                    (o) => o.value === previousAnswer.answer,
                  )?.label ?? previousAnswer.answer
                }
                details={previousAnswer.details || undefined}
                periodOrVersion={`v${previousVersion}`}
              />
            )}
          </div>

          {question.type === "text" ? (
            <textarea
              value={answer}
              onChange={(e) => onChange(question.id, "answer", e.target.value)}
              disabled={disabled}
              rows={3}
              className="input w-full min-h-[80px]"
              placeholder="Enter your answer…"
            />
          ) : question.options ? (
            renderOptions(question.options)
          ) : null}
        </div>
      </div>

      {lightboxImage &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightboxImage(null)}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImage(null);
              }}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
              aria-label="Close fullscreen image"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={lightboxImage}
              alt="Guidance fullscreen"
              className="max-w-full max-h-full object-contain rounded shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
